import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useCkPortal, useCkTheme } from "@NP-Webber/crm-kit/dist/next/index.js";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { AddReactionOutlinedIcon } from "../../ui/appIcons";

type Props = {
  disabled?: boolean;
  onPick: (emoji: string) => void;
};

type FixedPos = { top: number; left: number };

const HE_I18N = {
  search: "חיפוש אימוג'י",
  search_no_results_1: "אופס!",
  search_no_results_2: "לא נמצא אימוג'י",
  pick: "בחרו אימוג'י…",
  add_custom: "הוספת אימוג'י מותאם",
  categories: {
    activity: "פעילות",
    custom: "מותאם אישית",
    flags: "דגלים",
    foods: "אוכל ושתייה",
    frequent: "בשימוש תכוף",
    nature: "חיות וטבע",
    objects: "חפצים",
    people: "סמיילים ואנשים",
    places: "נסיעות ומקומות",
    search: "תוצאות חיפוש",
    symbols: "סמלים",
  },
  skins: {
    choose: "בחרו גוון עור ברירת מחדל",
    1: "ברירת מחדל",
    2: "בהיר",
    3: "בהיר בינוני",
    4: "בינוני",
    5: "כהה בינוני",
    6: "כהה",
  },
};

const PICKER_WIDTH = 320;
const PICKER_HEIGHT = 380;

function placeAboveButton(anchor: DOMRect): FixedPos {
  const gap = 8;
  const margin = 8;
  const spaceAbove = anchor.top - margin;
  const openAbove = spaceAbove >= PICKER_HEIGHT + gap;
  const top = openAbove
    ? Math.max(margin, anchor.top - PICKER_HEIGHT - gap)
    : Math.min(anchor.bottom + gap, window.innerHeight - PICKER_HEIGHT - margin);
  let left = anchor.left + anchor.width / 2 - PICKER_WIDTH / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - PICKER_WIDTH - margin));
  return { top, left };
}

export function ChatComposerEmojiPicker({ disabled, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<FixedPos | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const updatePos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    setPos(placeAboveButton(btn.getBoundingClientRect()));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    const onReposition = () => updatePos();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer, true);
    };
  }, [open]);

  const ckPortal = useCkPortal();
  const { resolvedMode } = useCkTheme();
  const portal =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            className="chat-composer-emoji-portal"
            style={{ top: pos.top, left: pos.left, width: PICKER_WIDTH }}
          >
            <Picker
              data={data}
              onEmojiSelect={(emoji: { native?: string }) => {
                if (emoji?.native) onPick(emoji.native);
                setOpen(false);
              }}
              theme={resolvedMode === "dark" ? "dark" : "light"}
              previewPosition="none"
              skinTonePosition="search"
              dynamicWidth={false}
              maxFrequentRows={2}
              perLine={8}
              i18n={HE_I18N}
            />
          </div>,
          ckPortal ?? document.body,
        )
      : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`chat-window__composer-emoji${open ? " is-open" : ""}`}
        aria-label="הוספת אימוג'י"
        title="הוספת אימוג'י"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <AddReactionOutlinedIcon />
      </button>
      {portal}
    </>
  );
}
